import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import * as React from 'react';

import AlertsCenterBase from '../../components/AlertsCenter/AlertsCenterBase';
import { useAlertsCenterData } from '../../components/AlertsCenter/useAlertsCenterData';
import { useTransitionedValue } from '../../hooks/transition/useTransitionedValue';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { useSidePanel } from '../../providers/SidePanelProvider';
import { getAlertsCardHeightPx } from '../../utils/alert/alertsCenterCard';
import { getWorkItemAlertRowAction } from '../../utils/alert/workItemAlertAction';
import { isWorkItemUnread } from '../../utils/alert/workItemAlertRow';
import { SIDE_PANEL_TRANSITION_MS } from '../../utils/sidePanelMotion';

import type { ListedAlert } from '../../utils/alert/alertRow';
import type { AlertsCenterPresentation, IAlertsCenterMotion } from '../../utils/alert/alertsCenterCard';

export interface IComposerAlertsCenterOptions {
  /**
   * Open the clicked alert in the assistant transcript. Supplied by the pane,
   * which owns the WebSocket — the card only decides *which* rows expand.
   */
  onExpandAlert: (alertId: string, wasUnread: boolean) => void;
}

/** A presentation change in flight, with the handler that settles it. */
export interface IAlertsCardMotion extends IAlertsCenterMotion {
  /** Attach to the element whose `height` transitions. */
  onTransitionEnd: React.TransitionEventHandler;
}

export interface IComposerAlertsCenter {
  /** The card in its current presentation, or `null` when the viewer has no center. */
  alertsSlot: React.ReactNode;
  /** The presentation the card is in, or moving to. */
  alertsPresentation: AlertsCenterPresentation;
  /** The presentation change in flight, or none at rest. */
  alertsMotion: IAlertsCardMotion | undefined;
  /**
   * The height the composer holds under the card: zero when the card is
   * absent, and while the card is full the last composer height it held, so
   * the input never moves under the cover.
   */
  alertsReservedHeightPx: number;
  /**
   * A failed reconnect's error line. Rendered above the input rather than in
   * the card: a fix row from the full center lands on the pill, which has no
   * room for it, and the truncated card renders no action slot at all.
   */
  composerNoticeSlot: React.ReactNode;
  /**
   * Whether the full center is covering the transcript and the composer —
   * from the first frame of its expand until the end of its collapse.
   */
  isCoveredByAlerts: boolean;
}

/**
 * The Notification Center as the assistant composer's card: the reads, the
 * presentation the side panel holds, and every click, returned as the pieces
 * `AssistantPaneView` places — a hook rather than a component because the card
 * and the reconnect-error line land in two different places in the view.
 *
 * The presentation on screen can differ from the one the panel holds in one
 * case: the truncated card with a positive count but no need-attention rows
 * fetched yet (an all-PENDING first page, or a failed live read) shows the
 * pill instead of nothing, and that pill opens the full center — whose loading
 * and error states are the only honest destination — rather than an empty
 * card. Whenever the truncated card has rows, the pill opens to it.
 *
 * Row clicks: a turn in flight refuses the click whole (the row dims under the
 * lock pill, nothing is marked read, the card stays put); otherwise the row is
 * marked read, a row with a fix of its own runs it and the card notes it, and
 * every other row opens in the thread through `onExpandAlert`. The pane's
 * opener is what moves the card after an expansion, so a record page's banner
 * hand-off moves it the same way.
 *
 * Focus follows the full center: opening it moves focus to the header chevron
 * and closing it returns focus to the control that opened it, when that
 * control still exists, once the collapse has settled. Escape closes it.
 *
 * Every presentation change is staged through `useTransitionedValue` at the
 * panel's duration, and the pieces the view needs to tween the card's height
 * come back as `alertsMotion`.
 *
 * @param options.onExpandAlert Opens an alert in the transcript — the pane's
 *   own opener, which is also where the card learns an alert entered the
 *   thread.
 * @returns The card element (or `null` when the viewer has no center), its
 *   presentation and the change in flight, the height the composer reserves
 *   under it, the reconnect-error line for the input region, and whether the
 *   full center is covering the pane.
 */
export function useComposerAlertsCenter({ onExpandAlert }: IComposerAlertsCenterOptions): IComposerAlertsCenter {
  const isAlertsCenterEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const {
    alertsPresentation,
    expandAlertsCenter,
    collapseAlertsCenter,
    viewAllAlertsCenter,
    openAlertsCenter,
    closeAlertsCenter,
    noteRowFixRan,
  } = useSidePanel();

  // History is only ever shown by the full center, and a session that never
  // opens it never pays for the read. Derived during render so the first full
  // render already issues the query.
  const [hasEverBeenFull, setHasEverBeenFull] = React.useState(alertsPresentation === 'full');
  if (alertsPresentation === 'full' && !hasEverBeenFull) {
    setHasEverBeenFull(true);
  }

  const {
    activeCount,
    sections,
    status,
    needAttentionRowCount,
    byId,
    isTurnInFlight,
    blockedNoticeAlertId,
    showBlockedNotice,
    markRead,
    runAction,
    alertActionSlot,
    handleDismiss,
    handleRetry,
  } = useAlertsCenterData({
    skip: !isAlertsCenterEnabled,
    skipHistory: alertsPresentation !== 'full' && !hasEverBeenFull,
  });

  const hasNeedAttentionRows = needAttentionRowCount > 0;
  const hasAnythingToReview = Math.max(activeCount, needAttentionRowCount) > 0;
  const presentation: AlertsCenterPresentation =
    alertsPresentation === 'truncated' && !hasNeedAttentionRows && hasAnythingToReview
      ? 'collapsed'
      : alertsPresentation;
  const isFull = presentation === 'full';

  const transition = useTransitionedValue(presentation, {
    transitionProperty: 'height',
    durationMs: SIDE_PANEL_TRANSITION_MS,
  });
  const isLeavingFull = transition.from === 'full';
  const isCoveredByAlerts = isFull || isLeavingFull;

  const cardHeightPx = getAlertsCardHeightPx({ presentation, activeCount, needAttentionRowCount });
  // The composer keeps the height it last held while the full center covers
  // it, so a collapse lands on a spacer that is already right.
  const [heldComposerHeightPx, setHeldComposerHeightPx] = React.useState(cardHeightPx);
  if (!isFull && heldComposerHeightPx !== cardHeightPx) {
    setHeldComposerHeightPx(cardHeightPx);
  }
  const alertsReservedHeightPx = isFull ? heldComposerHeightPx : cardHeightPx;

  const { from, stage, onTransitionEnd } = transition;
  const fromHeightPx =
    from === null || from === 'full'
      ? null
      : getAlertsCardHeightPx({ presentation: from, activeCount, needAttentionRowCount });
  const alertsMotion = React.useMemo<IAlertsCardMotion | undefined>(
    () =>
      from === null || stage === 'settled'
        ? undefined
        : { from, stage, fromHeightPx, toHeightPx: isFull ? null : cardHeightPx, onTransitionEnd },
    [from, stage, fromHeightPx, isFull, cardHeightPx, onTransitionEnd]
  );

  const collapseButtonRef = React.useRef<HTMLButtonElement>(null);
  const openerRef = React.useRef<Element | null>(null);

  // Both moves happen in effect bodies, never a cleanup: React puts the
  // pre-commit focus back after the mutation phase, which is when cleanups run.
  React.useLayoutEffect(() => {
    if (isFull) {
      openerRef.current = document.activeElement;
      collapseButtonRef.current?.focus();
    }
  }, [isFull]);

  // Focus returns once the region under the card is no longer inert, or the
  // move would be swallowed. "View all" unmounts with the truncated card, so
  // only an opener that outlived the full center (the bell) gets focus back.
  React.useLayoutEffect(() => {
    if (isCoveredByAlerts) {
      return;
    }

    const opener = openerRef.current;
    openerRef.current = null;
    if (opener instanceof HTMLElement && opener.isConnected) {
      opener.focus();
    }
  }, [isCoveredByAlerts]);

  React.useEffect(() => {
    if (!isFull) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        closeAlertsCenter();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFull, closeAlertsCenter]);

  const handleSelect = React.useCallback(
    (alert: ListedAlert) => {
      // Mid-turn, every row is locked — including ones whose click would only
      // navigate, because the user cannot tell those apart and a user-launched
      // Plaid flow could land on top of one the running turn opens. The click
      // is refused whole, so the row is not marked read either.
      if (isTurnInFlight) {
        showBlockedNotice(alert.id);
        return;
      }

      void markRead([alert.id]);

      // An alert the user can act on sends them at the fix instead of into the
      // transcript.
      const workItem = byId.get(alert.id);
      if (workItem && runAction(getWorkItemAlertRowAction(workItem))) {
        noteRowFixRan();
        return;
      }

      // Read off the item rather than the row: `markRead` above has not landed
      // yet, and the transcript needs to know whether this was new to the user.
      onExpandAlert(alert.id, workItem !== undefined && isWorkItemUnread(workItem));
    },
    [isTurnInFlight, showBlockedNotice, markRead, byId, runAction, noteRowFixRan, onExpandAlert]
  );

  if (!isAlertsCenterEnabled) {
    return {
      alertsSlot: null,
      alertsPresentation: presentation,
      alertsMotion: undefined,
      alertsReservedHeightPx: 0,
      composerNoticeSlot: null,
      isCoveredByAlerts: false,
    };
  }

  return {
    alertsSlot: (
      <AlertsCenterBase
        presentation={presentation}
        motion={alertsMotion}
        activeCount={activeCount}
        sections={sections}
        status={status}
        onExpand={hasNeedAttentionRows ? expandAlertsCenter : openAlertsCenter}
        onViewAll={viewAllAlertsCenter}
        onCollapse={collapseAlertsCenter}
        collapseButtonRef={collapseButtonRef}
        blockedNoticeAlertId={blockedNoticeAlertId}
        onSelect={handleSelect}
        onDismiss={handleDismiss}
        onRetry={handleRetry}
      />
    ),
    alertsPresentation: presentation,
    alertsMotion,
    alertsReservedHeightPx,
    composerNoticeSlot: alertActionSlot,
    isCoveredByAlerts,
  };
}
